import { ISchedule } from '@tableau/self-service-schedules/lib/src/components/ISchedule';
import { ActivitySpinnerSize, ActivitySpinnerWidget, FilteringList, ListItem } from '@tableau/widgets';
import * as React from 'react';
import _ = require('underscore');
import { Messages } from '../../codegen/Localize';
import { ReactActionDialogBodyProps } from '../../commonInfra/dialogs/ReactActionDialog';
import { cssClass } from '../../style';
import { ResourceQuery } from '../../ts/resources/ResourceQuery';
import { IScheduleInfo } from '../types/IScheduleInfo';

export interface SchedulePickerProps {
  readonly scheduledActionType: string;
  readonly useDescriptionAsName: boolean;
  readonly getSchedules: (matchText: string) => ResourceQuery<ISchedule>;
  readonly descriptionTranslateKey: string;
  readonly descriptionTranslateValues: { [key: string]: any };
}

export type PropsForSchedulePickerActionBody = ReactActionDialogBodyProps<SchedulePickerProps, ISchedule | undefined>;

export const INITIAL_SLICE_SIZE = 20;
export const SLICE_SIZE = 1000;

export function toListItem(useDescriptionAsName: boolean, schedule: ISchedule): ListItem {
  return {
    value: schedule.id,
    content: useDescriptionAsName
      ? IScheduleInfo.getScheduleDescription(schedule)
      : schedule.name,
  };
}

export function matchesSearch(searchText: string, item: ListItem): boolean {
  if (searchText === '') {
    return true;
  }
  const words = searchText.toLocaleLowerCase().split(' ');
  const name = item.content as string;
  return _.all(words, (word) => name.toLocaleLowerCase().indexOf(word) !== -1);
}

// Adapted from src\app\content\SetOwnerDialog.tsx for selecting Schedules
export function SchedulePickerActionBody(props: PropsForSchedulePickerActionBody) {
  const [searchText, setSearchText] = React.useState('');
  const [items, setItems] = React.useState([] as ISchedule[]);
  const [showSpinner, setShowSpinner] = React.useState(true);
  const [isError, setIsError] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState(undefined as string);

  const onSelectSchedule = React.useCallback(
    (value: string) => {
      const item = items.find((item) => item.id === value);
      if (item) {
        props.updateDialogState(item);
        setSelectedId(value);
      }
    },
    [props.updateDialogState, items],
  );

  const onSchedulePickerFilterChange = React.useCallback(
    (value: string) => setSearchText(value),
    [],
  );

  const hasSearchText = () => !_.isUndefined(searchText) && searchText.length !== 0;

  React.useEffect(
    () => {
      let ignore = false;
      async function fetchSchedules(start: number, size: number) {
        const isFirstFetch = items.length === 0;
        if (isFirstFetch) { // Starting from scratch, no errors
          setIsError(false);
        }
        try {
          const schedulesResult = await props.getSchedules(searchText).getSlice(start, size);
          const newItems: ISchedule[] = schedulesResult.items;
          if (!ignore) {
            setShowSpinner(false);
            setItems((items) => items.concat(newItems));
          }
          if (schedulesResult.total > start + newItems.length) {
            fetchSchedules(start + newItems.length, SLICE_SIZE);
          }
        } catch (e) {
          setShowSpinner(false);
          setIsError(true);
        }
      }

      fetchSchedules(0, INITIAL_SLICE_SIZE);
      return () => { ignore = true; };
    },
    // No dependence on search text since we do client side filtering
    []
  );
  const listMapper = (item: ISchedule) => toListItem(props.useDescriptionAsName, item);
  const listFilterer = (item: ListItem) => matchesSearch(searchText, item);
  let description = Messages.errorUnknown();
  try {
    const castKey = props.descriptionTranslateKey as keyof typeof Messages;
    const castValues = props.descriptionTranslateValues as any;
    description = Messages[castKey](castValues);
  } catch (e) {
    // Do nothing
  }

  /* eslint-disable jsx-a11y/no-autofocus */
  return <>
    <div style={{ paddingBottom: 12 }}>{description}</div>
    <div style={{ minHeight: 50 }}> {/* Needed to stop it from just collapsing when getting few/no results */}
      <FilteringList
        items={items.map(listMapper).filter(listFilterer)}
        onSelect={onSelectSchedule}
        searchPlaceholder={Messages.search()}
        testId="filtering-list-set-schedule"
        style={{ height: '100%', maxHeight: '300px', width: 'auto', border: '1px solid #b2b2b2' }}
        selectedValue={selectedId}
        onFilterChange={onSchedulePickerFilterChange}
        searchText={searchText}
        autoFocus={true}
        inputTestId="schedule-picker-filter"
        listTestId="schedule-picker-list"
      >
        {!showSpinner && (items.length === 0 || isError) &&
          <div className={noResults} data-tb-test-id="set-schedules-no-results">
            {hasSearchText() ? Messages.noMatchingResults() : Messages.noSchedulesAvailable()}
          </div>
        }
        {showSpinner &&
          <div className={spinner}>
            <ActivitySpinnerWidget
              spinnerSize={ActivitySpinnerSize.Large}
              testId={'owner-picker'}
            />
          </div>
        }
      </FilteringList>
    </div>
  </>;
}

const spinner = cssClass('set-owner-dialog', 'spinner', {
  marginTop: 18,
  textAlign: 'center'
});

const noResults = cssClass('set-owner-dialog', 'no-results', {
  fontSize: '15px',
  marginTop: 18,
  padding: 6,
  lineHeight: '23px',
  textAlign: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minHeight: 10,
});
